"use client"

import { useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { RefreshCw, CheckCircle2, Clock, AlertCircle, MinusCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { api } from "@/lib/api"
import type { SyncStatusResponse } from "@/lib/types"

function formatDateTime(iso: string | null): string {
  if (!iso) return "---"
  const d = new Date(iso)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

function formatTime(iso: string | null): string {
  if (!iso) return "---"
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

export function SyncStatusWidget() {
  const [data, setData] = useState<SyncStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get<SyncStatusResponse>("/dashboard/sync-status")
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const statusConfig = {
    completed: { label: "正常", icon: CheckCircle2, className: "bg-success text-success-foreground hover:bg-success/90" },
    syncing: { label: "同期中", icon: RefreshCw, className: "bg-primary text-primary-foreground hover:bg-primary/90" },
    never: { label: "未実行", icon: MinusCircle, className: "bg-secondary text-secondary-foreground" },
  }

  const status = data?.status ?? "never"
  const config = statusConfig[status]
  const StatusIcon = config.icon

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">データ同期状況</CardTitle>
          </div>
          {loading ? (
            <Skeleton className="h-6 w-16" />
          ) : (
            <Badge className={config.className}>
              <StatusIcon className="mr-1 h-3 w-3" />
              {config.label}
            </Badge>
          )}
        </div>
        <CardDescription>奉行クラウドとのAPI連携状況</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-secondary/50 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>最終同期成功日時</span>
                </div>
              </div>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {formatDateTime(data?.last_sync_at ?? null)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">同期間隔</p>
                <p className="mt-1 text-sm font-medium">
                  {data?.sync_interval_label ?? "---"}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">次回同期予定</p>
                <p className="mt-1 text-sm font-medium">
                  {formatTime(data?.next_sync_at ?? null)}
                </p>
              </div>
            </div>

            <Button variant="outline" className="w-full bg-transparent" size="sm">
              <RefreshCw className="mr-2 h-4 w-4" />
              今すぐ同期
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
